import React, { useEffect, useState } from "react";
import HealthCard from "./HealthCard";

/**
 * imgUrl from API is like "fleetOs/211793701955572-20261103-133244".
 * Download path uses the file name only: /api/uploads/download/{name}
 */
const resolveOfferImageUrl = (imgUrl) => {
  if (!imgUrl || typeof imgUrl !== "string") return null;
  const trimmed = imgUrl.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/api/")) {
    return trimmed;
  }
  const name = trimmed.includes("/")
    ? trimmed.split("/").pop()
    : trimmed.replace(/^fleetOs\//i, "");
  return name ? `/api/uploads/download/${name}` : null;
};

/**
 * fmd/24 data:
 * {
 *   offers: [{
 *     title, category, type, imgUrl, description,
 *     attributes: { expenseTypeName, expenseSubTypeName }
 *   }]
 * }
 */
const CampaignOffer = ({ translate, data, loading, onBook }) => {
  const offers = Array.isArray(data?.offers) ? data.offers : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [offers.length]);

  useEffect(() => {
    if (offers.length <= 1 || paused) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % offers.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [offers.length, index, paused]);

  const offer = offers[index] || null;
  const expenseTypeName =
    offer?.attributes?.expenseTypeName || offer?.expenseTypeName || "";
  const expenseSubTypeName =
    offer?.attributes?.expenseSubTypeName || offer?.expenseSubTypeName || "";
  const category = offer?.category || "";
  const type = (offer?.type || "").trim();
  const description = (offer?.description || "").trim();
  const image = resolveOfferImageUrl(
    offer?.imgUrl || data?.image || data?.imageUrl || data?.bannerUrl
  );

  const chips = [
    expenseTypeName && { key: "etype", label: expenseTypeName },
    expenseSubTypeName && { key: "esub", label: expenseSubTypeName },
    type && { key: "type", label: type },
  ].filter(Boolean);

  const goPrev = (e) => {
    e?.stopPropagation?.();
    if (!offers.length) return;
    setIndex((prev) => (prev - 1 + offers.length) % offers.length);
  };

  const goNext = (e) => {
    e?.stopPropagation?.();
    if (!offers.length) return;
    setIndex((prev) => (prev + 1) % offers.length);
  };

  return (
    <HealthCard
      className="fh-span-4"
      title={translate("exclusiveForYourFleet")}
      subtitle={translate("seasonalServiceOffer")}
      headerAction={
        category ? (
          <span className="fh-camp-cat fh-camp-cat--top">{category}</span>
        ) : null
      }
      badge={offers.length ? `${index + 1}/${offers.length}` : undefined}
      badgeStyle={{ background: "rgba(255,255,255,0.16)", color: "#fff" }}
      loading={loading}
      dark
    >
      {offers.length && offer ? (
        <>
          {offers.length > 1 ? (
            <>
              <button
                type="button"
                className="fh-camp-arrow left"
                onClick={goPrev}
                aria-label="Previous offer"
              >
                ‹
              </button>
              <button
                type="button"
                className="fh-camp-arrow right"
                onClick={goNext}
                aria-label="Next offer"
              >
                ›
              </button>
            </>
          ) : null}

          <div
            className="fh-camp-slider"
            key={offer.id || index}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="fh-camp-body">
              <div className="fh-camp-slide-title">{offer.title || "—"}</div>

              {description ? <p className="fh-camp-desc">{description}</p> : null}

              {chips.length ? (
                <div className="fh-camp-chips">
                  {chips.map((chip) => (
                    <span key={chip.key} className="fh-camp-chip">
                      {chip.label}
                    </span>
                  ))}
                </div>
              ) : null}

              {offers.length > 1 ? (
                <div className="fh-camp-dots">
                  {offers.map((item, i) => (
                    <button
                      key={item.id || i}
                      type="button"
                      className={`fh-camp-dot ${i === index ? "on" : ""}`}
                      onClick={() => setIndex(i)}
                      aria-label={`Offer ${i + 1}`}
                    />
                  ))}
                </div>
              ) : null}

              {/* <button
                type="button"
                className="fh-btn fh-camp-cta"
                onClick={() => onBook && onBook(offer)}
              >
                {translate("bookAppointment")}
              </button>
              */}
            </div>

            <div className={`fh-camp-hero ${image ? "has-img" : ""}`}>
              {image ? (
                <img src={image} alt={offer.title || "offer"} />
              ) : (
                <div className="fh-camp-hero-fallback">
                  {(offer.title || "C").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="fh-empty" style={{ color: "rgba(255,255,255,0.7)" }}>
          {translate("noCampaignAvailable")}
        </div>
      )}
    </HealthCard>
  );
};

export default CampaignOffer;
